import {
  AfterViewInit,
  Component,
  EventEmitter,
  NgZone,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { AuthService } from 'app/services/auth.service';
import { FacebookPixelService } from 'app/services/facebook-pixel.service';
import { TwitterPixelService } from 'app/services/twitter-pixel.service';
import { RedditPixelService } from 'app/services/reddit-pixel.service';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

declare const google: any;
declare const FB: any;
type AuthMode =
  | 'initial'
  | 'emailSignup'
  | 'login'
  | 'forgotPassword'
  | 'otpVerification'
  | 'resetPassword';

@Component({
  selector: 'app-auth-overlay',
  templateUrl: './auth-overlay.component.html',
  styleUrls: ['./auth-overlay.component.scss'],
})
export class AuthOverlayComponent implements OnInit, AfterViewInit {
 
  isAuthenticated = false;
  @Output() closeOverlay = new EventEmitter<void>();
  currentMode: AuthMode = 'initial';
  emailSignupForm!: FormGroup;
  loginForm!: FormGroup;
  forgotPasswordForm!: FormGroup;
  otpVerificationForm!: FormGroup;
  resetPasswordForm!: FormGroup;
  referralCode: string = '';

  // Flag for required password reset
  isRequiredPasswordReset: boolean = false;

  // Visibility toggles for password fields
  showPassword = false;
  showConfirmPassword = false;
  showNewPassword = false;
  showConfirmNewPassword = false;

  user: any = null;
  accessToken: string | null = null;

  // For OTP verification
  forgotPasswordEmail: string = '';
  otpSent: boolean = false;
  otpVerified: boolean = false;
  resendDisabled: boolean = false;
  resendCountdown: number = 60;
  resendTimerId: any;
  //loading state properties
  loginLoading: boolean = false;
  signupLoading: boolean = false;
  forgotPasswordLoading: boolean = false;
  otpVerificationLoading: boolean = false;
  resetPasswordLoading: boolean = false;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private fbPixel: FacebookPixelService,
    private twitterPixel: TwitterPixelService,
    private redditPixel: RedditPixelService,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private ngZone: NgZone
  ) {
    this.referralCode =
      this.route.snapshot.queryParamMap.get('referralCode') || '';
  }

  ngOnInit() {
    this.authService.authMode$.subscribe((signup) => {
    this.currentMode = signup==true?'emailSignup':'initial';
  });
    this.initializeForms();
  }

  close() {
    this.authService.showAuthOverlay(false);
  }

  // Initialize forms with validators
  private initializeForms() {
    this.emailSignupForm = this.fb.group(
      {
        firstName: ['', [Validators.required, Validators.minLength(2)]],
        lastName: ['', [Validators.required, Validators.minLength(2)]],
        email: [
          '',
          [Validators.required, Validators.email, this.emailValidator],
        ],
        password: ['', [Validators.required]],
        confirmPassword: ['', [Validators.required]],
      },
      {
        validators: [this.passwordMatchValidator],
      }
    );

    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email, this.emailValidator]],
      password: ['', [Validators.required]],
    });

    this.forgotPasswordForm = this.fb.group({
      email: ['', [Validators.required, Validators.email, this.emailValidator]],
    });

    this.otpVerificationForm = this.fb.group({
      otp: ['', [Validators.required, Validators.pattern(/^\d{4}$/)]],
    });

    this.resetPasswordForm = this.fb.group(
      {
        newPassword: [
          '',
          [
            Validators.required,
            Validators.minLength(6),
            Validators.pattern(/^(?=.*[A-Z])(?=.*[!@#$%^&*]).+$/),
          ],
        ],
        confirmNewPassword: ['', [Validators.required]],
      },
      {
        validators: [this.newPasswordMatchValidator],
      }
    );
  }

  // Custom validator for email
  private emailValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(value) ? null : { invalidEmail: true };
  }

  // Password match validator
  private passwordMatchValidator(form: FormGroup): ValidationErrors | null {
    const password = form.get('password');
    const confirmPassword = form.get('confirmPassword');

    if (password?.value !== confirmPassword?.value) {
      confirmPassword?.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }

    confirmPassword?.setErrors(null);
    return null;
  }

  // New password match validator
  private newPasswordMatchValidator(form: FormGroup): ValidationErrors | null {
    const newPassword = form.get('newPassword');
    const confirmNewPassword = form.get('confirmNewPassword');

    if (newPassword?.value !== confirmNewPassword?.value) {
      confirmNewPassword?.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }

    confirmNewPassword?.setErrors(null);
    return null;
  }

  // Toggle password visibility
  togglePasswordVisibility(
    field: 'password' | 'confirmPassword' | 'newPassword' | 'confirmNewPassword'
  ) {
    switch (field) {
      case 'password':
        this.showPassword = !this.showPassword;
        break;
      case 'confirmPassword':
        this.showConfirmPassword = !this.showConfirmPassword;
        break;
      case 'newPassword':
        this.showNewPassword = !this.showNewPassword;
        break;
      case 'confirmNewPassword':
        this.showConfirmNewPassword = !this.showConfirmNewPassword;
        break;
    }
  }

  signUpWithApple() {
    console.log('Signing up with Apple');
    // TODO: Implement Apple Sign In API
  }

  // Navigation Methods
  showEmailSignUp() {
    this.currentMode = 'emailSignup';
  }

  showLogin() {
    this.currentMode = 'login';
  }

  goBack() {
    // Don't allow going back when in required password reset mode
    if (this.isRequiredPasswordReset) {
      return;
    }

    if (
      this.currentMode === 'forgotPassword' ||
      this.currentMode === 'otpVerification' ||
      this.currentMode === 'resetPassword'
    ) {
      this.currentMode = 'login';
      // Clear any timers when navigating away
      if (this.resendTimerId) {
        clearInterval(this.resendTimerId);
      }
    } else {
      this.currentMode = 'initial';
    }
  }

  // Forgot Password Method
  forgotPassword() {
    this.currentMode = 'forgotPassword';
  }

  // Send OTP request
  requestOTP() {
    this.markFormGroupTouched(this.forgotPasswordForm);

    if (this.forgotPasswordForm.valid) {
      this.forgotPasswordLoading = true;

      this.forgotPasswordEmail =
        this.forgotPasswordForm.value.email.toLowerCase();

      this.authService
        .requestPasswordResetOTP(this.forgotPasswordEmail)
        .subscribe(
          (response) => {
            if (response.success) {
              this.otpSent = true;
              this.currentMode = 'otpVerification';
              this.startResendCountdown();
            } else {
              console.error('OTP request failed:', response.message);
              this.toastr.error('Error requesting verification code');
            }
            this.forgotPasswordLoading = false;
          },
          (error) => {
            this.toastr.error('Error requesting verification code');
            console.error('OTP request error:', error);
            this.forgotPasswordLoading = false;
          }
        );
    }
  }

  // Start countdown for OTP resend button
  startResendCountdown() {
    this.resendDisabled = true;
    this.resendCountdown = 60;

    this.resendTimerId = setInterval(() => {
      this.resendCountdown--;
      if (this.resendCountdown <= 0) {
        clearInterval(this.resendTimerId);
        this.resendDisabled = false;
      }
    }, 1000);
  }

  // Resend OTP
  resendOTP() {
    if (!this.resendDisabled) {
      this.forgotPasswordLoading = true;

      this.authService
        .requestPasswordResetOTP(this.forgotPasswordEmail)
        .subscribe(
          (response) => {
            if (response.success) {
              this.startResendCountdown();
              this.toastr.info(
                'A new verification code has been sent to your email'
              );
            } else {
              this.toastr.error(response.message);
              console.error('OTP resend failed:', response.message);
            }
            this.forgotPasswordLoading = false;
          },
          (error) => {
            this.toastr.error('Error resending verification code');
            console.error('OTP resend error:', error);
            this.forgotPasswordLoading = false;
          }
        );
    }
  }

  // Skip OTP verification and go directly to reset password
  skipToResetPassword(email: string) {
    this.forgotPasswordEmail = email;
    this.isRequiredPasswordReset = true;
    this.otpSent = true;
    this.currentMode = 'resetPassword';

    // Pre-fill the OTP verification form with hardcoded OTP
    this.otpVerificationForm.patchValue({
      otp: '1947',
    });

    this.otpVerified = true;
  }

  // Verify OTP
  verifyOTP() {
    this.markFormGroupTouched(this.otpVerificationForm);

    if (this.otpVerificationForm.valid) {
      this.otpVerificationLoading = true;

      const otp = this.otpVerificationForm.value.otp;

      this.authService.verifyOTP(this.forgotPasswordEmail, otp).subscribe(
        (response) => {
          if (response.success) {
            this.otpVerified = true;
            this.currentMode = 'resetPassword';

            // Add specific message if this is a required password reset
            if (this.isRequiredPasswordReset) {
              this.toastr.info(
                'Please set a new password that is different from your email address'
              );
            }

            // Clear any timers when OTP is verified
            if (this.resendTimerId) {
              clearInterval(this.resendTimerId);
            }
          } else {
            this.toastr.error('Invalid verification code');
            console.error('OTP verification failed:', response.message);
          }
          this.otpVerificationLoading = false;
        },
        (error) => {
          this.toastr.error('Error verifying code');
          console.error('OTP verification error:', error);
          this.otpVerificationLoading = false;
        }
      );
    }
  }

  // Reset Password
  resetPassword() {
    this.markFormGroupTouched(this.resetPasswordForm);

    // Add extra validation for password matching email
    if (this.isRequiredPasswordReset) {
      const newPassword = this.resetPasswordForm.value.newPassword;
      if (newPassword === this.forgotPasswordEmail) {
        this.resetPasswordForm
          .get('newPassword')
          ?.setErrors({ sameAsEmail: true });
        this.toastr.error(
          'Your new password cannot be the same as your email address'
        );
        return;
      }
    }

    if (this.resetPasswordForm.valid) {
      this.resetPasswordLoading = true;
      const newPassword = this.resetPasswordForm.value.newPassword;
      const otp = this.otpVerificationForm.value.otp;

      this.authService
        .resetPassword(otp, this.forgotPasswordEmail, newPassword)
        .subscribe(
          (response) => {
            if (response.success) {
              if (this.isRequiredPasswordReset) {
                // For required password reset, simply update auth status
                this.toastr.success('Password updated successfully');
                this.isRequiredPasswordReset = false;
              } else {
                // Regular password reset flow
                this.toastr.success(
                  'Password reset successful. Please log in with your new password.'
                );
              }
              this.currentMode = 'login';

              this.resetPasswordForm.reset();
            } else {
              this.toastr.error('Password reset failed');
              console.error('Password reset failed:', response.message);
            }
            this.resetPasswordLoading = false;
          },
          (error) => {
            this.toastr.error('Error resetting password');
            console.error('Password reset error:', error);
            this.resetPasswordLoading = false;
          }
        );
    }
  }

  // Form Submission Methods
  onEmailSignup() {
    // Mark all fields as touched to trigger validation display
    this.markFormGroupTouched(this.emailSignupForm);

    // Check form validity
    if (this.emailSignupForm.valid) {
      this.signupLoading = true;
      const formData = this.emailSignupForm.value;

      // Prepare data for backend
      const signupPayload = {
        CustomerName:
          formData.firstName.trim() + ' ' + formData.lastName.trim(),
        Email: formData.email.toLowerCase(),
        Password: formData.password,
        ReferralCode: this.referralCode,
      };

      this.authService.signUp(signupPayload).subscribe(
        (response) => {
          if (response.success) {
            this.accessToken = response.data?.accessToken?.token;
            const { customerEmailAddress, customerId } =
              response?.data?.customer;
            this.close();
            this.fbPixel.trackCompleteRegistration();
            this.twitterPixel.trackCompleteRegistration(
              customerEmailAddress,
              customerId
            );
            this.redditPixel.trackSignUp(customerEmailAddress, customerId);
          } else {
            console.error('Signup failed:', response.message);
            this.toastr.error(response.message || 'Signup failed');
            this.authService.updateAuthStatus(false);
          }
          this.signupLoading = false;
        },
        (error) => {
          console.error('Signup error:', error);
          this.toastr.error(error.message || 'Error creating account');
          this.signupLoading = false;
          this.authService.updateAuthStatus(false);
        }
      );

      // Reset form after successful submission
      this.emailSignupForm.reset();
      this.currentMode = 'initial';
    }
  }

  onLogin() {
    // Mark all fields as touched to trigger validation display
    this.markFormGroupTouched(this.loginForm);

    if (this.loginForm.valid) {
      this.loginLoading = true;
      const formData = this.loginForm.value;

      // Check if email and password are identical
      const isEmailPasswordSame =
        formData.email.toLowerCase() === formData.password;

      const loginPayload = {
        Email: formData.email.toLowerCase(),
        Password: formData.password,
      };

      // If email and password are identical, go directly to password reset
      if (isEmailPasswordSame) {
        this.loginLoading = false;
        this.skipToResetPassword(formData.email.toLowerCase());
        return;
      }

      // Otherwise, proceed with normal login
      this.authService.login(loginPayload).subscribe(
        (response) => {
          if (response.success) {
            this.accessToken = response.data?.accessToken?.token;
            this.close();
          } else {
            this.toastr.error(response.message || 'Login failed');
            console.error('Login failed:', response.message);
            this.authService.updateAuthStatus(false);
          }
          this.loginLoading = false;
        },
        (error) => {
          this.toastr.error(error.message || 'Login error');
          console.error('Login error:', error);
          this.authService.updateAuthStatus(false);
          this.loginLoading = false;
        }
      );

      // Reset form after login attempt
      this.loginForm.reset();
    }
  }

  // Method to initiate required password reset
  initiateRequiredPasswordReset() {
    this.forgotPasswordLoading = true;

    // Show message to user
    this.toastr.warning(
      'For security reasons, your password cannot be the same as your email. Please reset your password.'
    );

    // Use the existing OTP request method
    this.authService
      .requestPasswordResetOTP(this.forgotPasswordEmail)
      .subscribe(
        (response) => {
          if (response.success) {
            this.otpSent = true;
            this.currentMode = 'otpVerification';
            this.startResendCountdown();

            // Can't dismiss this dialog
            this.isRequiredPasswordReset = true;

            this.toastr.info(
              'A verification code has been sent to your email.'
            );
          } else {
            console.error('OTP request failed:', response.message);
            this.toastr.error('Error requesting verification code');
            // Fall back to normal authentication if OTP request fails
            this.isRequiredPasswordReset = false;
            this.authService.updateAuthStatus(true);
          }
          this.forgotPasswordLoading = false;
        },
        (error) => {
          this.toastr.error('Error requesting verification code');
          console.error('OTP request error:', error);
          this.forgotPasswordLoading = false;
          // Fall back to normal authentication if OTP request fails
          this.isRequiredPasswordReset = false;
          this.authService.updateAuthStatus(true);
        }
      );
  }

  // Utility method to mark all controls as touched
  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // Error message getters for form validation
  getErrorMessage(control: AbstractControl | null, fieldName: string): string {
    if (!control || !control.errors) return '';

    if (control.errors['required']) {
      return `${fieldName} is required`;
    }
    if (control.errors['email']) {
      return 'Invalid email format';
    }
    if (control.errors['minlength']) {
      return `${fieldName} must be at least ${control.errors['minlength'].requiredLength} characters`;
    }
    if (control.errors['passwordMismatch']) {
      return 'Passwords do not match';
    }
    if (control.errors['weakPassword']) {
      return 'Password must include uppercase, lowercase, number, and special character';
    }
    if (control.errors['invalidName']) {
      return `${fieldName} contains invalid characters`;
    }
    if (control.errors['invalidEmail']) {
      return 'Invalid email address';
    }
    if (control.errors['pattern']) {
      return 'Invalid format';
    }
    if (control.errors['sameAsEmail']) {
      return 'Password cannot be the same as your email address';
    }
    return '';
  }

  signUpWithGoogle() {
    this.authService.signUpWithGoogle();
  }

  
// signUpWithFacebook() {
//   this.authService.signUpWithFacebook();
// }

signUpWithFacebook() {
  FB.login((response: any) => {
    if (response.authResponse) {
      const accessToken = response.authResponse.accessToken;

      // Get user profile from Facebook
      FB.api('/me', { fields: 'id,name,email' }, (userInfo: any) => {
        // Call your backend API for signup/login
        this.authService.socialLogin({
          Provider: 'facebook',
          code: accessToken,          // <-- send token in 'code' field for reuse
          ReferralCode: this.referralCode,
          CustomerName: userInfo.name // optional, backend can also fetch
        }).subscribe(
          (response) => {
            if (response.success) {
              const jwt = response.data?.jwToken?.token;
              localStorage.setItem('access_token', jwt);
              localStorage.setItem('customerId', response.data?.customer?.customerId);
              localStorage.setItem('referralCode', response.data?.customer?.referralCode);
             this.ngZone.run(() => {
                this.authService.updateAuthStatus(true);
                this.authService.showAuthOverlay(false, false);
                this.authService.loadCustomer();
              });
             
            } else {
              this.toastr.error(response.message || 'Facebook login failed');
            }
          },
          (err) => {
            console.error('Social login error:', err);
            this.toastr.error('Error signing in with Facebook');
          }
        );
      });
    } else {
      console.log('User cancelled login or did not fully authorize.');
    }
  }, { scope: 'public_profile,email' });
}


  ngAfterViewInit(): void {
    // this.loadFacebookSDK();
  }

  // loadFacebookSDK() {
  //   if (!document.getElementById('facebook-jssdk')) {
  //     let script = document.createElement('script');
  //     script.id = 'facebook-jssdk';
  //     script.src = 'https://connect.facebook.net/en_US/sdk.js';
  //     script.async = true;
  //     script.defer = true;

  //     script.onload = () => {
  //       (window as any).FB.init({
  //         appId: environment.facebookClientId,
  //         cookie: true,
  //         xfbml: true,
  //         version: 'v18.0', // Use latest version
  //       });

  //       (window as any).FB.AppEvents.logPageView();
  //     };

  //     document.body.appendChild(script);
  //   }
  // }

  // signInWithFacebook() {
  //   FB.getLoginStatus((response: any) => {
  //     if (response.status === 'connected') {
  //       // Already logged in, get user details
  //       this.accessToken = response.authResponse.accessToken;
  //       this.fetchFacebookUserData();
  //     } else {
  //       // Request Facebook login
  //       FB.login(
  //         (res: any) => {
  //           if (res.authResponse) {
  //             this.accessToken = res.authResponse.accessToken;
  //             this.fetchFacebookUserData();
  //           }
  //         },
  //         { scope: environment.facebookScope }
  //       );
  //     }
  //   });
  // }

  // fetchFacebookUserData() {
  //   FB.api(
  //     environment.facebookApiUrl,
  //     { fields: environment.facebookApiFields },
  //     (userInfo: any) => {
  //       this.user = userInfo;
  //       this.authService
  //         .socialLogin({
  //           Provider: 'facebook',
  //           code: this.accessToken,
  //           CustomerName: this.user.name,
  //           ReferralCode: this.referralCode,
  //         })
  //         .subscribe(
  //           (response) => {
  //             if (response.success) {
  //               this.accessToken = response.data?.jwToken?.token;
  //               localStorage.setItem('access_token', this.accessToken);
  //               localStorage.setItem(
  //                 'referralCode',
  //                 response.data?.customer?.referralCode
  //               );
  //               localStorage.setItem(
  //                 'customerId',
  //                 response.data?.customer?.customerId
  //               );
  //               this.authService.updateAuthStatus(true);
  //             } else {
  //               console.error('Social login failed:', response.message);
  //               this.authService.updateAuthStatus(false);
  //             }
  //           },
  //           (error) => {
  //             console.error('Social login error:', error);
  //             this.authService.updateAuthStatus(false);
  //           }
  //         );
  //     }
  //   );
  // }
}
